import { useState, useMemo } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SmartToy as AgentIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { ToolCallCard } from './ToolCallCard';
import type { OrchestratorEvent } from './useOrchestratorStream';

interface SubAgentCardProps {
  callId: string;
  events: OrchestratorEvent[];
  allEvents?: Map<string, OrchestratorEvent[]>;
}

export const SubAgentCard = ({ callId, events }: SubAgentCardProps) => {
  const [expanded, setExpanded] = useState(true);

  const startEvent = events.find((e) => e.type === 'sub_agent_started');
  const completedEvent = events.find((e) => e.type === 'sub_agent_completed');
  const alias = (startEvent?.data.alias as string) ?? callId;
  const task = (startEvent?.data.task as string) ?? '';
  const depth = (startEvent?.data.depth as number) ?? 1;
  const isComplete = !!completedEvent;
  const isSuccess = completedEvent?.data.success as boolean | undefined;

  const streamedText = useMemo(() => {
    return events
      .filter((e) => e.type === 'sub_agent_token')
      .map((e) => e.data.token as string)
      .join('');
  }, [events]);

  const toolCalls = events.filter((e) => e.type === 'sub_agent_tool_call');

  const statusIcon = !isComplete
    ? <AgentIcon sx={{ fontSize: 16, color: 'info.main' }} />
    : isSuccess
      ? <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
      : <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: isComplete ? (isSuccess ? 'success.main' : 'error.main') : 'info.main',
        borderRadius: 1,
        my: 0.5,
        ml: depth > 1 ? 2 : 0,
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        {statusIcon}
        <Chip label={alias} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600, fontSize: 12 }} />
        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, ml: 0.5 }}>
          {task.length > 80 ? task.slice(0, 80) + '...' : task}
        </Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      {!isComplete && <LinearProgress sx={{ height: 2 }} />}
      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {task && (
            <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic', color: 'text.secondary' }}>
              {task}
            </Typography>
          )}

          {toolCalls.map((tc) => (
            <ToolCallCard
              key={tc.id}
              toolName={tc.data.toolName as string}
              args={tc.data.arguments as string}
              isRunning={!isComplete}
            />
          ))}

          {streamedText && (
            <Box
              sx={{
                mt: 1,
                p: 1,
                bgcolor: 'action.hover',
                borderRadius: 0.5,
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {streamedText}
            </Box>
          )}

          {completedEvent && !isSuccess && Boolean(completedEvent.data.errorMessage) && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {completedEvent.data.errorMessage as string}
            </Typography>
          )}

          {completedEvent && isSuccess && Boolean(completedEvent.data.result) && (
            <Box
              sx={{
                mt: 1,
                p: 1,
                bgcolor: 'success.main',
                color: 'success.contrastText',
                borderRadius: 0.5,
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              {(completedEvent.data.result as string).slice(0, 500)}
              {(completedEvent.data.result as string).length > 500 ? '...' : ''}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
